// Wraps the YouTube IFrame Player API in something a React component can own.
//
// The API is a global with a single global ready callback, so loading it is a
// module-level promise: two players on one page (dashboard replay plus a seek
// preview, say) must not race to define window.onYouTubeIframeAPIReady.

type YoutubePlayerHandle = {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
};

type YoutubeGlobal = {
  Player: new (
    element: HTMLElement | string,
    options: {
      // Selects the embed domain. Set to youtube-nocookie.com (spec §11.1).
      host?: string;
      videoId: string;
      playerVars: Record<string, number>;
      events: { onReady?: () => void; onStateChange?: (event: { data: number }) => void };
    }
  ) => YoutubePlayerHandle;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number; BUFFERING: number; CUED: number };
};

declare global {
  interface Window {
    YT?: YoutubeGlobal;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiPromise: Promise<YoutubeGlobal> | null = null;

function loadYoutubeApi(): Promise<YoutubeGlobal> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise<YoutubeGlobal>((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    window.onYouTubeIframeAPIReady = () => {
      if (window.YT?.Player) resolve(window.YT);
      else reject(new Error('YouTube API loaded without a Player constructor'));
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('Could not load the YouTube player'));
    document.head.appendChild(script);
  });
  return apiPromise;
}

export async function youtubeIframeController(input: {
  mount: HTMLElement;
  videoId: string;
  showControls: boolean;
  onEnded?: () => void;
}): Promise<{
  playVideo: () => void;
  pauseVideo: () => void;
  seekToMs: (ms: number) => void;
  currentTimeMs: () => number;
  durationMs: () => number;
  playerState: () => number;
  destroy: () => void;
}> {
  const YT = await loadYoutubeApi();

  const player = await new Promise<YoutubePlayerHandle>((resolve) => {
    const instance = new YT.Player(input.mount, {
      // youtube-nocookie materially reduces the tracking cookies YouTube sets on
      // reviewers who did not choose to be here (spec §11.1). One string, and the
      // player behaves identically — but playsinline, fs and disablekb are
      // re-verified against this domain rather than assumed to carry over.
      host: 'https://www.youtube-nocookie.com',
      videoId: input.videoId,
      playerVars: {
        // playsinline is the load-bearing one: without it iOS hijacks playback
        // into native fullscreen, which destroys the letterbox margin the icon
        // columns live in — and with it the entire tap interface.
        playsinline: 1,
        // Same reason from the other direction: no fullscreen button, because we
        // own the layout and there is nothing useful on the other side of it.
        fs: 0,
        // The number keys 1-9 are ours (spec §5). Left enabled, YouTube would
        // treat them as seek-to-percent and jump the video on every desktop tap.
        disablekb: 1,
        rel: 0,
        modestbranding: 1,
        controls: input.showControls ? 1 : 0
      },
      events: {
        onReady: () => resolve(instance),
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED) input.onEnded?.();
        }
      }
    });
  });

  // Every accessor is wrapped: the API throws if the iframe is mid-teardown or
  // cross-origin handshake has not settled, and a throw inside a 500ms sampler
  // or a requestAnimationFrame loop would take down the whole session.
  const safely = <T>(read: () => T, fallback: T): T => {
    try {
      return read();
    } catch {
      return fallback;
    }
  };

  return {
    playVideo: () => safely(() => player.playVideo(), undefined),
    pauseVideo: () => safely(() => player.pauseVideo(), undefined),
    seekToMs: (ms: number) => safely(() => player.seekTo(ms / 1000, true), undefined),
    currentTimeMs: () => Math.max(0, Math.round(safely(() => player.getCurrentTime(), 0) * 1000)),
    durationMs: () => Math.max(0, Math.round(safely(() => player.getDuration(), 0) * 1000)),
    playerState: () => safely(() => player.getPlayerState(), -99),
    destroy: () => safely(() => player.destroy(), undefined)
  };
}
